import * as React from 'react';
import { CircularProgress } from '@startuptools/ui/CircularProgress';
import { Box } from '@startuptools/ui/Box';
import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { GenericError } from '@startuptools/ui/Error';
import { Stack } from '@startuptools/ui/Stack';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { useWppCreateMutation, useWppUpdateMutation } from '../../../../../../../graphql/react-operations';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { OptionsSubscriberForm } from './form';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { type GqlCompany, type GqlCreateIdentityInput } from '../../../../../../../graphql/base-types.graphql';
import { type EditWarrantParticipant } from './types';
import { PaymentKind } from '@startuptools/common/participants';
import { isNil } from 'lodash-es';
import { type DocumentNode } from 'graphql';
import { CustomVestingForm } from './CustomVesting';
import { shareClassToInput } from '../../../../../../../helpers/share-class-to-input';
import { useWppDefaultsSuspenseQuery } from './wpp-defaults.graphql';

const Inner = ({
  closeDialog,
  optionRegistrationId,
  enableEditQuantity = true,
  identity,
  participant,
  paymentKind,
  company,
  refetchQueries,
  infoBanner,
  warrantProgram,
}: {
  identity?: GqlCreateIdentityInput;
  optionRegistrationId: string;
  closeDialog: () => void;

  enableEditQuantity?: boolean;
  paymentKind: PaymentKind;
  participant?: EditWarrantParticipant;
  company: Pick<GqlCompany, 'id' | 'allowShortQeso' | 'organizationNumber'>;
  refetchQueries: (string | DocumentNode)[];
  infoBanner?: React.ReactNode;
  warrantProgram: React.ComponentProps<typeof OptionsSubscriberForm>['warrantProgram'];
}) => {
  const companyId = useCompanyId();

  const displayIdentity = identity ?? participant?.identity;
  const defaultIdentityData = participant ? undefined : displayIdentity;

  const { data } = useWppDefaultsSuspenseQuery({
    variables: {
      warrantProgramId: warrantProgram.id,
      identityId: defaultIdentityData?.id || participant?.identity.id,
    },
  });

  const [create, { loading: createLoading, error: createError }] = useWppCreateMutation({ refetchQueries });
  const [mutate, { loading: updateLoading, error: updateError }] = useWppUpdateMutation({ refetchQueries });
  const [showCustomVestingForm, setShowCustomVestingForm] = React.useState(false);

  const onSubmit: React.ComponentProps<typeof OptionsSubscriberForm>['onSubmit'] = async formData => {
    const {
      vestingKind,
      vestingDuration,
      vestingFrequency,
      vestingCliffDuration,
      vestingAcceleration,
      repurchaseVested,
      quantity,
      claim,
      email,
      phone,
      forcedRedemption,
      shareClass,
      leaveOfAbsence,
      leaveOfAbsenceDays,
    } = formData;
    const sc = warrantProgram.shareClasses.find(sc => sc.name === shareClass) ||
      participant?.shareClass || {
        name: 'STAM',
        voteWeight: '1',
      };

    if (!participant) {
      if (isNil(paymentKind) || !identity) {
        throw new Error('Missing props for create mode');
      }
      await create({
        variables: {
          companyId,
          input: {
            identityInput: identityToInput(identity, email, phone),
            shareClass: shareClassToInput(sc),
            optionsRegistrationId: optionRegistrationId,
            quantity,
            claim: paymentKind === PaymentKind.Offset ? claim : null,
            paymentKind,
          },
        },
      });
    } else {
      await mutate({
        variables: {
          companyId,
          input: {
            identityInput: identityToInput(participant.identity, email, phone),
            id: participant.id,
            shareClass: shareClassToInput(sc),
            quantity,
            claim: paymentKind === PaymentKind.Offset ? claim : null,
            forcedRedemption: forcedRedemption ?? participant.forcedRedemption,
            vestingKind: vestingKind ?? participant.vestingKind,
            vestingDuration: vestingDuration ?? 0,
            vestingFrequency: vestingFrequency ?? 0,
            vestingCliffDuration: vestingCliffDuration ?? 0,
            vestingAcceleration: vestingAcceleration ?? participant.vestingAcceleration,
            repurchaseVested: repurchaseVested ?? participant.repurchaseVested,
            leaveOfAbsence: leaveOfAbsence ?? participant.leaveOfAbsence,
            leaveOfAbsenceDays: leaveOfAbsenceDays ?? participant.leaveOfAbsenceDays,
          },
        },
      });
    }
  };

  if (!displayIdentity) {
    throw new Error('Missing identity');
  }

  return (
    <>
      {(createLoading ?? updateLoading) && (
        <Box
          sx={{
            position: 'absolute',
            zIndex: 1,
            background: 'white',
            left: '0',
            right: '0',
            bottom: '0',
            top: '0',
            opacity: '.5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Stack gap={3}>
        <Identity identity={displayIdentity} editable={false} showAvatar />
        <Box>
          {showCustomVestingForm ? (
            <CustomVestingForm
              wp={warrantProgram}
              warrantParticipant={participant!}
              saveWpp={async wpp => {
                await mutate({ variables: { companyId, input: { id: participant!.id, ...wpp } } });
              }}
              vestingOccasions={participant!.vestingOccasions}
              close={() => {
                setShowCustomVestingForm(false);
              }}
              refetchQueries={refetchQueries}
            />
          ) : (
            <OptionsSubscriberForm
              defaultValues={{
                vestingDuration: participant?.vestingDuration ?? data.wppDefaults.vestingDuration,
                vestingKind: participant?.vestingKind ?? data.wppDefaults.vestingKind,
                vestingAcceleration: participant?.vestingAcceleration ?? data.wppDefaults.vestingAcceleration,
                vestingCliffDuration: participant?.vestingCliffDuration ?? data.wppDefaults.vestingCliffDuration,
                vestingFrequency: participant?.vestingFrequency ?? data.wppDefaults.vestingFrequency,
                repurchaseVested: participant?.repurchaseVested ?? data.wppDefaults.repurchaseVested,
                forcedRedemption: participant?.forcedRedemption ?? data.wppDefaults.forcedRedemption,
                paymentMethod: participant?.paymentKind ?? paymentKind,
                shareClass: participant?.shareClass.name ?? warrantProgram.shareClasses?.[0]?.name ?? 'STAM',
                claim: participant?.claim ?? '',
                quantity: participant?.quantity ?? '',
                email: participant?.identity.email ?? defaultIdentityData?.email ?? '',
                phone: participant?.identity.phone ?? defaultIdentityData?.phone ?? '',
                leaveOfAbsence: participant?.leaveOfAbsence ?? data.wppDefaults.leaveOfAbsence,
                leaveOfAbsenceDays: participant?.leaveOfAbsenceDays ?? data.wppDefaults.leaveOfAbsenceDays,
              }}
              infoBanner={infoBanner}
              toggleCustomVestingForm={() => {
                setShowCustomVestingForm(true);
              }}
              enableEditQuantity={enableEditQuantity}
              company={company}
              warrantProgram={warrantProgram}
              onSubmit={onSubmit}
              closeDialog={closeDialog}
              error={(updateError || createError) && <GenericError />}
              participant={participant}
              paymentKind={participant?.paymentKind ?? paymentKind}
            />
          )}
        </Box>
      </Stack>
    </>
  );
};

export const EditParticipantDialog = ({ closeDialog, ...rest }: React.ComponentProps<typeof Inner>) => {
  return (
    <Modal open onClose={closeDialog}>
      <ModalDialog sx={{ overflowY: 'auto' }}>
        <Box>
          <React.Suspense fallback={<CircularProgress />}>
            <Inner closeDialog={closeDialog} {...rest} />
          </React.Suspense>
        </Box>
      </ModalDialog>
    </Modal>
  );
};
